/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {Component, FC, ReactNode} from 'react';
import Grid from '@material-ui/core/Grid';
import {makeStyles, Theme, useTheme} from '@material-ui/core/styles';
import classnames from 'classnames';
import ConditionalRender from '../../render/ConditionalRender';
import {GridLayoutSizes} from '../../types/components';

interface IonContentType {
  classes?: {
    [key: string]: string;
  };
  children: ReactNode;
  inlineContent?: ReactNode;
  overlayLeft?: ReactNode;
  overlayRight?: ReactNode;
  gridLayoutSizes?: GridLayoutSizes;
  useExpandedWidth?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  gridContainerRoot : {
    position : 'relative'
  },
  collapsedGridContainerItem : {
    '&&' : {
      padding : '0'
    }
  },
  expandedGridContainerItem : {
    '&&' : {
      padding : '0'
    },
    [theme.breakpoints.up('md')] : {
      flexBasis : '96%',
      maxWidth  : '96%'
    }
  },
  gridOverlayItem : {
    height   : '100%',
    position : 'absolute',
    width    : '25%',
    '&&'     : {
      padding : '0'
    },
    [theme.breakpoints.down('sm')] : {
      width : '100%'
    }
  },
  leftOverlay : {
    left : '0'
  },
  rightOverlay : {
    right : '0'
  }
}));

/**
 * Grid Component to match gid displays in the DLS
 *
 * ```typescript
 *  <IonContent overlayLeft={<LeftOverlay />}>
 *    <PageContent />
 *  </IonContent>
 *
 *  <IonContent overlayRight={<RightOverlay />}>
 *    <PageContent />
 *  </IonContent>
 *
 *  <IonContent overlayLeft={<LeftOverlay />}
 *    overlayRight={<RightOverlay />}>
 *    <PageContent />
 *  </IonContent>
 *
 *  <IonContent inlineContent={<InlineContent />}>
 *    <PageContent />
 *  </IonContent>
 *
 * ```
 */
export const IonContent = ({
  children,
  classes: additionalClasses,
  inlineContent,
  overlayLeft,
  overlayRight,
  gridLayoutSizes,
  useExpandedWidth = false
}: IonContentType) => {
  const classes = useStyles();
  // Cannot have both overlays and inline children together
  const onlyOverlays = (overlayLeft || overlayRight) && !inlineContent;
  const onlyInline = !overlayLeft && !overlayRight && inlineContent;

  return (
    <Grid container
      justifyContent='center'
      className={classes.gridContainerRoot}
      classes={{...additionalClasses}}>
      {
        onlyOverlays && (
          <>
            {overlayLeft && (
              <Grid item
                className={classnames(classes.leftOverlay, classes.gridOverlayItem)}
                xs={12}
                md={3}>
                {overlayLeft}
              </Grid>
            )}
            <Grid item
              className={classes.collapsedGridContainerItem}
              xs={12}
              md={10}>
              {children}
            </Grid>
            {overlayRight && (
              <Grid item
                className={
                  classnames(classes.rightOverlay, classes.gridOverlayItem)
                }
                xs={12}
                md={3}>
                {overlayRight}
              </Grid>
            )}
          </>
        )
      }
      {
        onlyInline && (
          <>
            <Grid item
              className={classes.collapsedGridContainerItem}
              xs={12}
              md={4}>
              {inlineContent}
            </Grid>
            <Grid item
              className={classes.collapsedGridContainerItem}
              xs={12}
              md={8}>
              {children}
            </Grid>
          </>
        )
      }
      <ConditionalRender condition={(!onlyInline && !onlyOverlays)}>
        <Grid className={useExpandedWidth ? classes.expandedGridContainerItem : classes.collapsedGridContainerItem}
          xs={gridLayoutSizes?.xs ?? 12}
          md={gridLayoutSizes?.md ?? 10}
          item>
          {children}
        </Grid>
      </ConditionalRender>
    </Grid>
  );
};

export default IonContent;
